import { toast } from "sonner";

import "@/styles/ai-engine.css";

import { Lock, LucideIcon } from "lucide-react";
import { Spinner } from "./UI/Spinner";

type ISpotStructureProps = {
  image?: string;
  icon?: LucideIcon;
  title?: string;
  description: string;
};

type ISpotToastProps = string | ISpotStructureProps;

type ToastAction = {
  label: string;
  onClick: () => any;
};

export const SpotToast = {
  // Success toast
  success: (
    myProps: ISpotToastProps,
    action?: ToastAction,
    duration?: number,
    id?: string | number
  ) =>
    toast.success(
      typeof myProps === "string"
        ? myProps
        : myProps.title || myProps.description,
      {
        description:
          typeof myProps === "string" ? undefined : myProps.description,
        action,
        actionButtonStyle: {
          backgroundColor: "#039855",
        },
        duration,
        id,
        icon: undefined, // Reset icon if updating from a loading toast
      }
    ),
  // Error toast
  error: (
    myProps: ISpotToastProps,
    action?: ToastAction,
    duration?: number,
    id?: string | number
  ) =>
    toast.error(
      typeof myProps === "string"
        ? myProps
        : myProps.title || myProps.description,
      {
        description:
          typeof myProps === "string" ? undefined : myProps.description,
        action,
        actionButtonStyle: {
          backgroundColor: "#D92D20",
        },
        duration,
        id,
        icon: undefined, // Reset icon if updating from a loading toast
      }
    ),
  // Warning toast
  warn: (
    myProps: ISpotToastProps,
    action?: ToastAction,
    duration?: number,
    id?: string | number
  ) =>
    toast.warning(
      typeof myProps === "string"
        ? myProps
        : myProps.title || myProps.description,
      {
        description:
          typeof myProps === "string" ? undefined : myProps.description,
        action,
        actionButtonStyle: {
          backgroundColor: "#DC6803",
        },
        duration,
        id,
        icon: undefined, // Reset icon if updating from a loading toast
      }
    ),
  // Info toast
  info: (
    myProps: ISpotToastProps,
    action?: ToastAction,
    duration?: number,
    id?: string | number
  ) =>
    toast.info(
      typeof myProps === "string"
        ? myProps
        : myProps.title || myProps.description,
      {
        description:
          typeof myProps === "string" ? undefined : myProps.description,
        action,
        actionButtonStyle: {
          backgroundColor: "#396E97",
        },
        duration,
        id,
        icon: undefined, // Reset icon if updating from a loading toast
      }
    ),
  // Loading toast
  loading: (myProps: ISpotToastProps, id?: string | number) =>
    toast(
      typeof myProps === "string"
        ? myProps
        : myProps.title || myProps.description,
      {
        description:
          typeof myProps === "string" ? undefined : myProps.description,
        id,
        icon: <Spinner size="sm" />,
        duration: 30000,
      }
    ),

  // Permission-denied toast — used by the global 403 interceptor and
  // anywhere a permission gate blocks the user. Distinct from the
  // generic error toast because the cause is fixable by an admin
  // granting a role, not by retrying.
  permissionDenied: (message?: string) =>
    toast.error("You don't have permission to do that", {
      description:
        message ||
        "Ask a workspace admin to grant you the required role or permission.",
      icon: <Lock size={16} className="text-destructive" />,
      duration: 6000,
    }),
};
